import { memo, useCallback, useRef, useMemo, useEffect, KeyboardEvent } from 'react';
import classNames from 'classnames';
import { V2QsEqVasProps, defaultV2QsEqVasProps } from './types';

// Scale constants
const SCALE_MIN = 0;
const SCALE_MAX = 100;
const LABEL_INTERVAL = 5;

// SVG layout constants — numbers LEFT, vertical line RIGHT
const SVG_WIDTH = 72;
const LINE_X = 68; // vertical line near right edge
const MAJOR_TICK_W = 16; // tick length for multiples of 5
const MINOR_TICK_W = 8; // tick length for others
const NUMBER_X = LINE_X - MAJOR_TICK_W - 4; // number label right edge (text-anchor: end)
const PADDING_Y = 10; // top/bottom padding inside SVG

/**
 * V2QsEqVas 컴포넌트
 *
 * EQ-VAS (EuroQol Visual Analogue Scale) - 표준 PROM 문제 유형
 * 세로 눈금자(0~100) 위에서 환자가 건강 상태를 X로 표시하는 컴포넌트
 *
 * 주요 기능:
 * - 세로 눈금자 (0~100, 5 간격 숫자, 1 간격 눈금)
 * - 클릭/드래그로 값 선택
 * - 선택한 값이 좌측 박스에 자동 표시
 * - 상/하단 라벨 표시
 * - 키보드 접근성 (↑↓ 키)
 */
function V2QsEqVas(props: V2QsEqVasProps) {
  const {
    value,
    onChange,
    topLabel = defaultV2QsEqVasProps.topLabel,
    bottomLabel = defaultV2QsEqVasProps.bottomLabel,
    valueBoxLabel = defaultV2QsEqVasProps.valueBoxLabel,
    title,
    leftText,
    scaleHeight = defaultV2QsEqVasProps.scaleHeight!,
    disabled = defaultV2QsEqVasProps.disabled,
    readOnly = defaultV2QsEqVasProps.readOnly,
    hasError = defaultV2QsEqVasProps.hasError,
    errorMessage,
    className,
  } = props;

  const trackRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  const isInteractive = !disabled && !readOnly;
  const isValueSet = value !== undefined && value !== null;
  const svgHeight = scaleHeight;
  const scaleAreaHeight = svgHeight - PADDING_Y * 2;

  // Convert scale value (0-100) to SVG Y coordinate
  const valueToY = useCallback(
    (v: number): number => {
      return PADDING_Y + ((SCALE_MAX - v) / (SCALE_MAX - SCALE_MIN)) * scaleAreaHeight;
    },
    [scaleAreaHeight],
  );

  // Convert clientY (browser coordinate) to scale value (0-100)
  const clientYToValue = useCallback(
    (clientY: number): number => {
      if (!trackRef.current) return 0;
      const rect = trackRef.current.getBoundingClientRect();
      const relY = clientY - rect.top;
      // Account for padding
      const clampedY = Math.max(PADDING_Y, Math.min(relY, PADDING_Y + scaleAreaHeight));
      const ratio = 1 - (clampedY - PADDING_Y) / scaleAreaHeight;
      return Math.max(SCALE_MIN, Math.min(SCALE_MAX, Math.round(ratio * (SCALE_MAX - SCALE_MIN))));
    },
    [scaleAreaHeight],
  );

  // Mouse event handlers
  const handlePointerDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isInteractive) return;
      isDraggingRef.current = true;
      const newValue = clientYToValue(e.clientY);
      onChange?.(newValue);
    },
    [isInteractive, clientYToValue, onChange],
  );

  // Document-level mouse move/up for smooth dragging
  useEffect(() => {
    const handleMouseMove = (e: globalThis.MouseEvent) => {
      if (!isDraggingRef.current || !isInteractive) return;
      const newValue = clientYToValue(e.clientY);
      onChange?.(newValue);
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isInteractive, clientYToValue, onChange]);

  // Touch event handlers
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!isInteractive) return;
      isDraggingRef.current = true;
      const touch = e.touches[0];
      const newValue = clientYToValue(touch.clientY);
      onChange?.(newValue);
    },
    [isInteractive, clientYToValue, onChange],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDraggingRef.current || !isInteractive) return;
      e.preventDefault(); // prevent scrolling while dragging
      const touch = e.touches[0];
      const newValue = clientYToValue(touch.clientY);
      onChange?.(newValue);
    },
    [isInteractive, clientYToValue, onChange],
  );

  const handleTouchEnd = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  // Keyboard handlers for accessibility
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isInteractive) return;
      const currentValue = value ?? 50;
      let newValue: number | null = null;

      switch (e.key) {
        case 'ArrowUp':
          newValue = Math.min(SCALE_MAX, currentValue + 1);
          break;
        case 'ArrowDown':
          newValue = Math.max(SCALE_MIN, currentValue - 1);
          break;
        case 'PageUp':
          newValue = Math.min(SCALE_MAX, currentValue + 5);
          break;
        case 'PageDown':
          newValue = Math.max(SCALE_MIN, currentValue - 5);
          break;
        case 'Home':
          newValue = SCALE_MAX;
          break;
        case 'End':
          newValue = SCALE_MIN;
          break;
        default:
          return;
      }

      if (newValue !== null) {
        e.preventDefault();
        onChange?.(newValue);
      }
    },
    [isInteractive, value, onChange],
  );

  // Generate tick data
  const ticks = useMemo(() => {
    const result: { value: number; y: number; isLabeled: boolean }[] = [];
    for (let v = SCALE_MAX; v >= SCALE_MIN; v--) {
      result.push({
        value: v,
        y: valueToY(v),
        isLabeled: v % LABEL_INTERVAL === 0,
      });
    }
    return result;
  }, [valueToY]);

  // Marker Y position
  const markerY = isValueSet ? valueToY(value!) : null;

  return (
    <div className={classNames('flex gap-6', className)}>
      {/* Left Column: Title + Instruction Text + Value Box */}
      <div className='flex flex-col flex-1 min-w-0 justify-center'>
        {/* Title */}
        {title && (
          <div className='text-[15px] font-medium text-[#222638] leading-relaxed mb-2'>
            {title}
          </div>
        )}

        {/* Instruction text */}
        {leftText && (
          <div className='text-sm text-[#222638] leading-relaxed whitespace-pre-wrap'>
            {leftText}
          </div>
        )}

        {/* Value display box */}
        <div className={classNames(leftText ? 'mt-6' : 'mt-0')}>
          <div className='flex items-center gap-3'>
            <p className='text-sm text-[#222638] whitespace-nowrap'>{valueBoxLabel}</p>
            <div
              className={classNames(
                'w-20 h-12 flex items-center justify-center border-2 rounded text-xl font-bold transition-colors',
                isValueSet
                  ? 'border-[#222638] text-[#222638]'
                  : 'border-[#DADDEB] text-[#AFB2C2]',
                hasError && 'border-error',
              )}
            >
              {isValueSet ? value : ''}
            </div>
          </div>
        </div>

        {/* Error message */}
        {hasError && errorMessage && <p className='text-error text-sm mt-2'>{errorMessage}</p>}
      </div>

      {/* Right Column: Vertical Scale */}
      <div className='flex flex-col items-end shrink-0'>
        {/* Top label */}
        <p className='text-xs text-right text-[#222638] mb-1 leading-tight whitespace-pre-line max-w-[120px]'>
          {topLabel}
        </p>

        {/* Scale interactive area */}
        <div
          ref={trackRef}
          className={classNames(
            'relative select-none',
            isInteractive ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
          )}
          style={{ width: SVG_WIDTH, height: svgHeight }}
          onMouseDown={handlePointerDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          // Accessibility
          role='slider'
          aria-valuemin={SCALE_MIN}
          aria-valuemax={SCALE_MAX}
          aria-valuenow={isValueSet ? value : undefined}
          aria-label='EQ-VAS health state scale'
          tabIndex={isInteractive ? 0 : -1}
          onKeyDown={handleKeyDown}
        >
          <svg
            width={SVG_WIDTH}
            height={svgHeight}
            viewBox={`0 0 ${SVG_WIDTH} ${svgHeight}`}
            className='pointer-events-none'
          >
            {/* Vertical line */}
            <line
              x1={LINE_X}
              y1={PADDING_Y}
              x2={LINE_X}
              y2={PADDING_Y + scaleAreaHeight}
              stroke='#333333'
              strokeWidth={1}
            />

            {/* Tick marks and number labels */}
            {ticks.map((tick) => {
              const tickW = tick.isLabeled ? MAJOR_TICK_W : MINOR_TICK_W;
              return (
                <g key={tick.value}>
                  {/* Horizontal tick mark (extends left from vertical line) */}
                  <line
                    x1={LINE_X - tickW}
                    y1={tick.y}
                    x2={LINE_X}
                    y2={tick.y}
                    stroke='#333333'
                    strokeWidth={1}
                  />
                  {/* Number label (left of tick marks) */}
                  {tick.isLabeled && (
                    <text
                      x={NUMBER_X}
                      y={tick.y}
                      fontSize={11}
                      fill='#333333'
                      dominantBaseline='central'
                      textAnchor='end'
                    >
                      {tick.value}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Marker at selected value — filled triangle pointer */}
            {markerY !== null && (
              <g className='pointer-events-none'>
                {/* Horizontal indicator line */}
                <line
                  x1={LINE_X - MAJOR_TICK_W - 2}
                  y1={markerY}
                  x2={LINE_X}
                  y2={markerY}
                  stroke='#4B7BF5'
                  strokeWidth={2.5}
                />
                {/* Triangle arrow pointing right */}
                <polygon
                  points={`${LINE_X - MAJOR_TICK_W - 10},${markerY - 5} ${LINE_X - MAJOR_TICK_W - 10},${markerY + 5} ${LINE_X - MAJOR_TICK_W - 2},${markerY}`}
                  fill='#4B7BF5'
                />
              </g>
            )}
          </svg>
        </div>

        {/* Bottom label */}
        <p className='text-xs text-right text-[#222638] mt-1 leading-tight whitespace-pre-line max-w-[120px]'>
          {bottomLabel}
        </p>
      </div>
    </div>
  );
}

export default memo(V2QsEqVas);
